/**
 * One row of the Registered People panel.
 *
 * ─── WHY THIS IS A COMPONENT AND NOT MARKUP IN TWO PAGES ───
 * The local and cloud investigation pages each drew their own copy, and the copies had already
 * started to differ. The DATA plumbing genuinely differs between hosts (one talks to the node
 * directly, one through a tunnel), but the card does not — so the card moved here and takes
 * callbacks, and the hosts keep the part that is actually host-specific.
 *
 * ─── WHAT THE OLD CARD GOT WRONG ───
 * Four badges competing on one line and four controls crammed beside the name, with no
 * hierarchy between them. Two specific failures worth not repeating:
 *
 *   · "Employee" and "Unmark" as button labels. "Employee" is a NOUN — it reads as a filter or
 *     a state, not as "make this person one" — and "Unmark" never says unmark WHAT. Employment
 *     is now a labelled switch: the label names the property, the switch shows the state, and
 *     flipping it is self-evidently the action. It is the one control here that changes what
 *     the system DOES with somebody, so it is also the most prominent.
 *
 *   · `hover:bg-muted/50`. The theme's colours are bare `var()`, and Tailwind v3 emits NO rule
 *     for an opacity modifier on those — the hover state simply did not exist. Every tint here
 *     is either a numeric palette colour (which supports opacity) or inline color-mix.
 */
import type { ReactNode } from 'react';
import {
  UserCircleIcon, TrashIcon, NoSymbolIcon, PhotoIcon, ChevronRightIcon,
} from '@heroicons/react/24/outline';
import {
  watchlistSeverityOf, watchlistCategoryOf, PERSON_WATCHLIST_CATEGORIES,
} from '../common/watchlistForm';
import { useEnrolledPhoto } from './useEnrolledPhoto';

export interface RegisteredPerson {
  person_id: string;
  name?: string | null;
  label?: string | null;
  notes?: string | null;
  is_employee?: boolean;
  employee_id?: string | null;
  is_blacklisted?: boolean;
  blacklist_reason?: string | null;
  /** blacklisted | wanted | missing. Migration 055; older rows default to blacklisted. */
  blacklist_category?: string | null;
  blacklist_severity?: string | null;
  blacklist_alert?: boolean;
  vector_registered?: boolean;
  photo_count?: number;
  /** The first enrolled face. The image is fetched on demand — see useEnrolledPhoto. */
  primary_photo_id?: string | null;
  face_image_url?: string | null;
  [key: string]: unknown;
}

export interface RegisteredPersonCardProps {
  person: RegisteredPerson;
  /**
   * Override the face image. Normally omitted: the card resolves `primary_photo_id` itself
   * through the injected API, which is the one path that works on both dashboards.
   */
  imageUrl?: string | null;
  canEdit?: boolean;
  /** Whether the enrolled-faces drawer under the card is open. */
  expanded?: boolean;
  onToggleExpand?: () => void;
  onToggleEmployee?: () => void;
  onToggleBlacklist?: () => void;
  onDelete?: () => void;
  /** True while a classification call is in flight — disables the switch and says so. */
  busy?: boolean;
  /** The enrolled-faces list, rendered by the host because its image handling differs. */
  children?: ReactNode;
}

/** Initials, for a person with no photo on file. Two letters at most. */
function initials(name?: string | null): string {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '';
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

const titleCase = (v: string) => v.charAt(0).toUpperCase() + v.slice(1);

/**
 * The employment control: a label, a switch, and one line saying what it means.
 *
 * Deliberately NOT an icon or a bare word. Of everything on this card it is the only control
 * whose effect an operator cannot guess from the result — attendance either is or is not
 * calculated for this person from now on — so it is the one that spells itself out.
 */
function EmployeeSwitch({
  on, disabled, busy, onChange,
}: { on: boolean; disabled?: boolean; busy?: boolean; onChange?: () => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={on ? 'Remove the employment record' : 'Mark as an employee'}
      disabled={disabled || busy}
      onClick={onChange}
      title={on
        ? 'Attendance is being calculated. Switching off removes the employment record — the person and their face stay registered.'
        : 'Switch on to make this person an employee. Attendance is calculated from then on; their face does not need enrolling again.'}
      className={`flex h-[26px] items-center gap-1.5 rounded-lg border px-2 text-left transition-colors ${
        on ? 'border-brand/40 bg-brand/10' : 'border-border hover:bg-muted'
      } ${disabled || busy ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
    >
      <span
        aria-hidden
        className={`relative inline-flex h-3.5 w-6 shrink-0 items-center rounded-full transition-colors ${
          on ? 'bg-brand' : 'bg-muted-foreground/40'
        }`}
      >
        <span
          className={`inline-block h-2.5 w-2.5 transform rounded-full bg-white shadow transition-transform ${
            on ? 'translate-x-3' : 'translate-x-0.5'
          }`}
        />
      </span>
      {/* One line, not two. The consequence still has to be said — it is the whole reason
          this control is not a bare word — but it belongs in the row's caption below, where
          it costs no height per card. */}
      <span className={`whitespace-nowrap text-[11px] font-medium ${on ? 'text-brand' : 'text-foreground'}`}>
        {busy ? 'Working…' : 'Employee'}
      </span>
    </button>
  );
}

export default function RegisteredPersonCard({
  person, imageUrl, canEdit = false, expanded = false,
  onToggleExpand, onToggleEmployee, onToggleBlacklist, onDelete, busy = false, children,
}: RegisteredPersonCardProps) {
  // Resolved HERE rather than passed in. The hosts used to each supply their own URL, and the
  // cloud one supplied none at all — so every cloud row showed initials however many photos
  // the person had. One resolver, both dashboards.
  const resolved = useEnrolledPhoto(person.primary_photo_id);
  const face = imageUrl ?? resolved;
  const name = person.name || person.person_id;
  const photos = person.photo_count ?? 0;
  const enrolled = !!person.vector_registered;
  const flagged = !!person.is_blacklisted;
  // A flagged person whose alerting is off is a real and different state: visible in reports,
  // silent on the wall. Collapsing the two would make an operator think alerts were coming.
  const silent = flagged && person.blacklist_alert === false;
  const severity = watchlistSeverityOf(person.blacklist_severity);
  const category = watchlistCategoryOf(PERSON_WATCHLIST_CATEGORIES, person.blacklist_category);

  return (
    <div className={`overflow-hidden rounded-xl border bg-background transition-colors ${
      flagged ? 'border-red-500/40' : 'border-border'
    }`}>
      {/* ── Identity ── */}
      <div className="flex items-start gap-3 p-3">
        {face ? (
          <img
            src={face}
            alt=""
            className="h-11 w-11 shrink-0 rounded-lg border border-border object-cover"
          />
        ) : (
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-border bg-muted">
            {initials(person.name) ? (
              <span className="text-[13px] font-semibold text-muted-foreground">{initials(person.name)}</span>
            ) : (
              <UserCircleIcon className="h-6 w-6 text-muted-foreground" />
            )}
          </div>
        )}

        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-semibold text-foreground">{name}</p>
          {/* ONE secondary line instead of a row of competing badges: what kind of person they
              are, and whether the cameras can actually recognise them. */}
          <p className="mt-0.5 truncate text-[11px] text-muted-foreground">
            {titleCase(person.label || 'person')}
            {' · '}
            <span className={enrolled ? 'text-emerald-500' : 'text-amber-500'}>
              {enrolled ? 'Face enrolled' : photos > 0 ? 'Enrolment pending' : 'No face on file'}
            </span>
          </p>
          {person.notes && (
            <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{person.notes}</p>
          )}
        </div>

        {canEdit && onDelete && (
          <button
            type="button"
            onClick={onDelete}
            title="Delete this person and remove their face vectors"
            aria-label={`Delete ${name}`}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-red-500/10 hover:text-red-500"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* ── Watchlist, on its own line where it cannot be missed ── */}
      {flagged && (
        <div className={`mx-3 mb-2 flex items-start gap-2 rounded-lg px-2.5 py-1.5 ${
          category?.banner || 'bg-red-500/10 text-red-500'
        }`}>
          <NoSymbolIcon className="mt-px h-3.5 w-3.5 shrink-0" />
          <p className="min-w-0 text-[11px] leading-snug">
            {/* WHAT they are listed as leads the line, because it is what the operator has to
                act on — "Missing" and "Blacklisted" call for opposite responses and used to
                read identically here. An entry from before categories existed, or one with an
                unrecognised value, falls back to the word this banner always said. */}
            <span className="font-semibold">
              {silent
                ? `${category?.label || 'Flagged'} (alerts off)`
                : (category?.label || 'Blacklisted')}
            </span>
            {/* The severity, in the same words and the same tint the vehicle watchlist uses —
                a row on either list should be readable by the same rules. Only where it means
                something: a flag with alerting off has no severity to describe. */}
            {!silent && severity && severity.value !== 'critical' && (
              <span className={`ml-1.5 rounded px-1 py-px text-[10px] font-medium ${severity.chip}`}>
                {severity.label}
              </span>
            )}
            {person.blacklist_reason ? ` — ${person.blacklist_reason}` : ''}
          </p>
        </div>
      )}

      {/* ── Actions, grouped and named ── */}
      {canEdit && (
        <div className="border-t border-border px-3 py-2">
          <div className="flex items-center gap-1.5">
            <EmployeeSwitch on={!!person.is_employee} busy={busy} onChange={onToggleEmployee} />

            <span className="ml-auto flex shrink-0 items-center gap-1">
              <button
                type="button"
                onClick={onToggleBlacklist}
                className={`flex h-[26px] items-center gap-1 rounded-lg px-1.5 text-[11px] font-medium transition-colors ${
                  flagged
                    ? 'text-red-500 hover:bg-red-500/10'
                    : 'text-muted-foreground hover:bg-muted hover:text-red-500'
                }`}
              >
                <NoSymbolIcon className="h-3.5 w-3.5" />
                {flagged ? 'Unflag' : 'Blacklist'}
              </button>
              <button
                type="button"
                onClick={onToggleExpand}
                aria-expanded={expanded}
                className="flex h-[26px] items-center gap-1 rounded-lg px-1.5 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <PhotoIcon className="h-3.5 w-3.5" />
                {photos > 0 ? photos : '—'}
                <ChevronRightIcon className={`h-3 w-3 transition-transform ${expanded ? 'rotate-90' : ''}`} />
              </button>
            </span>
          </div>
          {/* The consequence, under the row rather than inside the switch — said once, at the
              height of a line of text instead of doubling the control. */}
          <p className="mt-1 text-[10px] leading-tight text-muted-foreground">
            {person.is_employee
              ? 'Attendance is counted for this person.'
              : 'Registered only — in/out log, no attendance.'}
          </p>
        </div>
      )}

      {/* ── The enrolled faces, supplied by the host ── */}
      {expanded && children && (
        <div className="border-t border-border px-3 py-2">{children}</div>
      )}
    </div>
  );
}
